import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PDFPreview from '../documents/PDFPreview';
import { MButton } from '@/components/ui/atoms/MButtons';
import { useDocument } from '@/hooks/useDocument';
import { getMemberInfo } from '@/hooks/useMembers';
import { RootState } from '@/store';
import MSpacer from '@/components/ui/atoms/MSpacer';
import MLoader from '@/components/ui/atoms/MLoader';
import { triggerNativeShare } from '@/utils/nativeBridge';
import { triggerNativeEmergencyShare } from '@/utils/nativeBridge';
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from '@/constants/analytics.constants';

const EmergencyCardViewer: React.FC = () => {
  const location = useLocation();
  const loggedInMember = getMemberInfo();
  const selectedMemberFromRedux = useSelector((state: RootState) => state.auth.selectedMember);

  // Get memberId from: location state > Redux selectedMember > localStorage > logged-in member
  const memberIdFromState = location.state?.memberId;
  const selectedMemberFromStorage = localStorage.getItem('selectedMember');
  const parsedStorageMember = selectedMemberFromStorage ? JSON.parse(selectedMemberFromStorage) : null;

  const memberId = memberIdFromState ||
    selectedMemberFromRedux?.id ||
    parsedStorageMember?.id ||
    loggedInMember.id;

  const memberInfo = memberIdFromState || selectedMemberFromRedux || parsedStorageMember || loggedInMember;
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { downloadEmergencyCardPdf, fetchEmergencyCardFileKey } = useDocument(memberId || '');
  const [fileKey, setFileKey] = useState<string | undefined>(undefined);
  
  // Track page load once
  useEffect(() => {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.EMERGENCY_CARD,
      screenName: ANALYTICS_SCREEN_NAMES.EMERGENCY_CARD_VIEW,
    });
  }, []);
  

  useEffect(() => {
    if (!memberId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    let url: string | null = null;
    const fetchAll = async () => {
      try {
        const [blob, meta] = await Promise.all([
          downloadEmergencyCardPdf(),
          fetchEmergencyCardFileKey()
        ]);
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setPdfUrl(url);
        if (meta?.fileKey) setFileKey(meta.fileKey);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load emergency card PDF');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAll();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [memberId]);

  const handleNativeShare = async () => {
    // Track share button click
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.EMERGENCY_CARD,
      screenName: ANALYTICS_SCREEN_NAMES.EMERGENCY_CARD_SHARE,
    });
    
    try {
      triggerNativeEmergencyShare({ memberId, memberName: memberInfo.firstName });
    } catch (e) {
      console.error('[EmergencyCardViewer] Error triggering native share', e);
    }
  };



  return (
    <div className="flex flex-col items-center w-full min-h-screen ">
      <div className='heading-h7-bold'>Emergency Card</div>
      <MSpacer mt={16} />
      <div className="w-full max-w-2xl mx-auto ">
        {loading && <MLoader />}
        {pdfUrl && <PDFPreview fileUrl={pdfUrl} controlsPosition="bottom" />}
      </div>
      <div className="dashboardEmergencyShareContainer confirm-footer">
        <MButton
          variant="filled"
          size="md"
          className='w-full'
          onClick={handleNativeShare}
        >
          Share
        </MButton>
      </div>
    </div>
  );
};

export default EmergencyCardViewer;
